import { useCallback, useState } from 'react';
import { formatPence, type LedgerApproval } from '@london-life/contracts';
import { Badge } from '@london-life/ui/components/badge';
import { Button } from '@london-life/ui/components/button';
import { ConfirmDialog } from '@london-life/ui/admin/confirm-dialog';
import { DataTable } from '@london-life/ui/admin/data-table';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../../lib/api';
import { useAdmin } from '../../lib/admin-context';
import { when } from '../../lib/format';
import { useRequest } from '../../lib/use-request';

const what = (approval: LedgerApproval) =>
  approval.action === 'reversal'
    ? `Reverse a transaction of ${formatPence(approval.amountPence)}`
    : `${approval.direction === 'credit' ? 'Add' : 'Remove'} ${formatPence(approval.amountPence)}`;

// large corrections and reversals wait here; a different admin approves or rejects each one (audited)
export function ApprovalsPage() {
  const { admin, can } = useAdmin();
  const list = useRequest(useCallback(() => api.admin.economy.approvals(), []));
  const [deciding, setDeciding] = useState<{ approval: LedgerApproval; approve: boolean } | null>(null);
  return (
    <section>
      <PageHeader
        title="Approvals"
        description="Large corrections and reversals need a second admin. You cannot approve your own requests."
      />
      {list.error && <ErrorNotice error={list.error} onRetry={() => void list.reload()} />}
      <DataTable
        rows={list.data?.approvals ?? null}
        loading={list.busy}
        rowKey={(approval) => approval.id}
        empty="Nothing has needed approval yet."
        columns={[
          { key: 'when', header: 'Asked', render: (approval) => when(approval.createdAt) },
          { key: 'what', header: 'Change', render: (approval) => <strong>{what(approval)}</strong> },
          { key: 'reason', header: 'Reason', render: (approval) => approval.reason },
          { key: 'by', header: 'Asked by', render: (approval) => approval.requestedBy.email ?? '—' },
          {
            key: 'status',
            header: 'Status',
            render: (approval) =>
              approval.status === 'pending' ? (
                <Badge>Waiting</Badge>
              ) : (
                <Badge variant="secondary">
                  {approval.status === 'approved' ? 'Approved' : 'Rejected'} by {approval.decidedBy?.email ?? '—'}
                </Badge>
              ),
          },
          {
            key: 'actions',
            header: '',
            className: 'text-right',
            render: (approval) =>
              approval.status === 'pending' &&
              can('economy.write') &&
              approval.requestedBy.id !== admin.user.id && (
                <div className="admin-page-actions justify-end">
                  <Button size="sm" onClick={() => setDeciding({ approval, approve: true })}>
                    Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setDeciding({ approval, approve: false })}>
                    Reject
                  </Button>
                </div>
              ),
          },
        ]}
      />
      <ConfirmDialog
        open={deciding !== null}
        title={deciding?.approve ? 'Approve this change?' : 'Reject this change?'}
        description={
          deciding
            ? `${what(deciding.approval)}. Reason given: ${deciding.approval.reason}. ${
                deciding.approve ? 'The money moves straight away.' : 'Nothing moves.'
              }`
            : ''
        }
        confirmLabel={deciding?.approve ? 'Approve' : 'Reject'}
        danger={deciding?.approve}
        onClose={() => setDeciding(null)}
        onConfirm={async () => {
          if (!deciding) return;
          await api.admin.economy.decide(deciding.approval.id, { approve: deciding.approve });
          await list.reload();
        }}
      />
    </section>
  );
}
